import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

import { ErrorBoundary } from "@/components/error-boundary";
import { ForensicPauseBanner } from "@/components/forensic-pause-banner";
import { ForensicRecoveryDialog } from "@/components/forensic-recovery-dialog";
import { SHELL_AMBIENT_WASH } from "@/components/premium";
import { fetchWardLiftQuestions, prepareWardLiftChallenge } from "@/lib/forensic-recovery-lift-challenge";
import { useForensicPauseLift } from "@/lib/use-forensic-pause-lift";
import { useIsMobile } from "@/lib/use-media";
import { AppShellHeader } from "./app-shell-header";
import { BottomTabs } from "./bottom-tabs";
import { CredentialAddedPushListener } from "./credential-added-push-listener";
import { HandshakePushListener } from "./handshake-push-listener";
import { InvitationPushListener } from "./invitation-push-listener";
import { LadderDowngradePushListener } from "./ladder-downgrade-push-listener";
import { PendingInvitationsProvider } from "./pending-invitations-provider";
import { RecoveryPushListener } from "./recovery-push-listener";
import { RemovalHoldPushListener } from "./removal-hold-push-listener";
import { Sidebar } from "./sidebar";

const SHELL_STYLE = { backgroundImage: SHELL_AMBIENT_WASH };

interface AppShellLayoutProps {
  children: ReactNode;
  displayName?: string;
  aal?: "AAL1" | "AAL2" | "AAL3";
}

/** Supplies the shared invitation cache to both navigation layouts. */
export function AppShellLayout(props: AppShellLayoutProps): React.ReactElement {
  return <PendingInvitationsProvider><AppShellBody {...props} /></PendingInvitationsProvider>;
}

function AppShellBody({ children, displayName, aal }: AppShellLayoutProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [isRecoveryOpen, setRecoveryOpen] = useState(false);
  const [location] = useLocation();
  const recovery = useForensicPauseLift();
  const pauseActive = recovery.recoveryStatus?.pause === "active";
  const attempt = recovery.recoveryStatus?.attempt;
  // #3042 Part B item 3: the ward's own read of whether this incident's lift
  // ceremony is question-gated. Only fetched once a pause is actually active
  // — the questions read is meaningless (and the server may reject it)
  // otherwise.
  const liftQuestionsQuery = useQuery({
    queryKey: ["security", "forensic-recovery", "lift-questions"],
    queryFn: fetchWardLiftQuestions,
    enabled: pauseActive,
    staleTime: 15_000,
    retry: false,
  });

  const banner = (
    <div className="relative z-[60]">
      {/* `!= null` (loose): an unresolved status leaves `attempt` undefined,
          and `undefined !== null` would claim a recovery is in progress (#2681
          review). Invisible while `active` is false, but the prop must not lie. */}
      <ForensicPauseBanner active={pauseActive} inProgress={attempt != null}
        onChooseRecovery={() => setRecoveryOpen(true)} />
    </div>
  );
  const dialog = recovery.recoveryStatus ? (
    <ForensicRecoveryDialog open={isRecoveryOpen} onOpenChange={setRecoveryOpen}
      status={recovery.recoveryStatus} busy={recovery.isRecoveryBusy}
      error={recovery.recoveryError}
      liftQuestions={liftQuestionsQuery.data}
      onBuildChallenge={liftQuestionsQuery.data
        ? (raw) => prepareWardLiftChallenge(liftQuestionsQuery.data.guardians, raw)
        : undefined}
      liftQuestionsPending={pauseActive && liftQuestionsQuery.isPending}
      liftQuestionsErrored={pauseActive && liftQuestionsQuery.isError}
      liftQuestionsFetching={liftQuestionsQuery.isFetching}
      onRetryLiftQuestions={() => { void liftQuestionsQuery.refetch(); }}
      onStartPath={(path, challenge) => {
        if (challenge) { void recovery.startRecovery(path, challenge); return; }
        void recovery.startRecovery(path);
      }}
      onContinue={(attemptId) => { void recovery.continueRecovery(attemptId); }}
      onRetryGuardian={(attemptId, challenge) => {
        if (challenge) { void recovery.continueRecovery(attemptId, challenge); return; }
        void recovery.continueRecovery(attemptId);
      }}
      onSubmitCode={(attemptId, code) => { void recovery.submitRecoveryCode(attemptId, code); }}
      onCancel={(attemptId) => { void recovery.cancelRecovery(attemptId); }}
      onVeto={(attemptId) => { void recovery.vetoRecovery(attemptId); }} />
  ) : null;
  const listeners = (
    <><InvitationPushListener /><HandshakePushListener /><RecoveryPushListener />
      <RemovalHoldPushListener /><CredentialAddedPushListener /><LadderDowngradePushListener /></>
  );
  const content = <ErrorBoundary resetKey={location} label="this page">{children}</ErrorBoundary>;

  if (isMobile) {
    return (
      <div data-testid="app-shell-mobile" className="min-h-dvh bg-bg-primary" style={SHELL_STYLE}>
        {banner}{dialog}{listeners}
        <AppShellHeader aal={aal} forensicPauseActive={pauseActive} />
        <main className="px-4 pb-[calc(var(--bottom-tabs-h)+16px)]">
          <div className="mx-auto max-w-[640px]">{content}</div>
        </main>
        <BottomTabs displayName={displayName} aal={aal} />
      </div>
    );
  }

  return (
    <div className="flex h-dvh bg-bg-primary">
      <Sidebar aal={aal} />
      <div data-testid="app-shell-content-column"
        className="flex flex-1 flex-col overflow-hidden" style={SHELL_STYLE}>
        {banner}{dialog}{listeners}
        <AppShellHeader aal={aal} forensicPauseActive={pauseActive} />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[960px] px-6 py-6">{content}</div>
        </main>
      </div>
    </div>
  );
}
